/* eslint-disable max-lines-per-function */

import { Country } from '@dreryk/address-common-api';
import { HealthcareProviderInvoiceData } from '@dreryk/egabinet-healthcare-provider-frontend-api';

import { useFormController } from '@dreryk/react-common';
import { Form, Grid, GridAreas, Heading, StyledFlex } from '@dreryk/react-components';

import { BankAccountNumberField } from '../../../components/BankAccountNumberField';
import { BankNameField } from '../../../components/BankNameField';
import { BdoNumberField } from '../../../components/BdoNumberField';
import { BuildingField } from '../../../components/BuildingField';
import { CompanyNameField } from '../../../components/CompanyNameField';
import { EmailField } from '../../../components/EmailField';
import { FlatField } from '../../../components/FlatField';
import { PasswordField } from '../../../components/PasswordField';
import { PostcodeField } from '../../../components/PostcodeField';
import { StreetField } from '../../../components/StreetField';
import { TaxIdentifierField } from '../../../components/TaxIdentifierField';
import { TownField } from '../../../components/TownField';
import { invoiceCreateAccountModalTranslationKeys } from '../../../translations';

import { InvoiceCreateAccountFormBean, InvoiceCreateAccountFormController } from './InvoiceCreateAccountForm.types';
import { InvoiceCreateAccountFormPrivacyPolicyAndGdprCheckbox } from './InvoiceCreateAccountFormPrivacyPolicyAndGdprCheckbox';
import { InvoiceCreateAccountFormTermsAndConditionsCheckbox } from './InvoiceCreateAccountFormTermsAndConditionsCheckbox';
import { InvoiceLegalAndComplianceLoader } from './InvoiceLegalAndCompliance.loader';

type ADDRESS_GRID_AREA = 'town' | 'postcode' | 'street' | 'building' | 'flat';
type DETAILS_GRID_AREA = 'taxIdentifier' | 'bdoNumber' | 'bankName' | 'bankAccountNumber';
type LOGIN_DETAILS_GRID_AREA = 'email' | 'password';

const ADDRESS_GRID_COLUMNS = 3;
const DETAILS_GRID_COLUMNS = 3;
const LOGIN_DETAILS_GRID_COLUMNS = 2;
const GRID_GAP = '24px 36px';

const ADDRESS_GRID_AREAS: GridAreas<ADDRESS_GRID_AREA, typeof ADDRESS_GRID_COLUMNS> = {
   custom: {
      template: ['town postcode .', 'street building flat'],
      gridTemplateColumns: '308px 128px 128px',
      gap: GRID_GAP,
   },
};

const DETAILS_GRID_AREAS: GridAreas<DETAILS_GRID_AREA, typeof DETAILS_GRID_COLUMNS> = {
   custom: {
      template: ['taxIdentifier bdoNumber .', 'bankName bankAccountNumber bankAccountNumber'],
      gridTemplateColumns: '256px 256px 1fr',
      gap: GRID_GAP,
   },
};

const LOGIN_DETAILS_GRID_AREAS: GridAreas<LOGIN_DETAILS_GRID_AREA, typeof LOGIN_DETAILS_GRID_COLUMNS> = {
   custom: {
      template: ['email password', '. .'],
      gridTemplateColumns: '358px 358px',
      gap: GRID_GAP,
   },
};

type InvoiceCreateAccountFormRenderProps = Pick<InvoiceCreateAccountFormController, 'handleSubmit' | 'formState'>;

type InvoiceCreateAccountFormRenderer = (renderProps: InvoiceCreateAccountFormRenderProps) => JSX.Element;

type InvoiceCreateAccountFormProps = {
   readonly currentHealthcareProviderInvoiceData: HealthcareProviderInvoiceData;
   readonly children: InvoiceCreateAccountFormRenderer;
};

export const InvoiceCreateAccountForm = (props: InvoiceCreateAccountFormProps): JSX.Element => {
   const { children, currentHealthcareProviderInvoiceData } = props;

   const { register, handleSubmit, formState } = useFormController<InvoiceCreateAccountFormBean>({
      building: currentHealthcareProviderInvoiceData.address.findBuilding(),
      companyName: currentHealthcareProviderInvoiceData.name,
      flat: currentHealthcareProviderInvoiceData.address.findFlat(),
      postcode: currentHealthcareProviderInvoiceData.address.findPostcode(),
      street: currentHealthcareProviderInvoiceData.address.findStreet(),
      // TODO: DREGAB-19275 - tutaj jest problem z komponentem TaxIdentifier
      taxIdentifier: currentHealthcareProviderInvoiceData.taxIdentifier.asString(),
      town: currentHealthcareProviderInvoiceData.address.findTown(),
   });

   return (
      <Form>
         <StyledFlex direction='column' gap={40}>
            <div>
               <Heading
                  content={invoiceCreateAccountModalTranslationKeys.companyDetails}
                  testId={'companyDetails'}
                  type='h2'
               />
               <StyledFlex direction='column' gap={24}>
                  <CompanyNameField register={register} />
                  <Grid<ADDRESS_GRID_AREA, typeof ADDRESS_GRID_COLUMNS>
                     areas={ADDRESS_GRID_AREAS}
                     testId={'addressGrid'}
                     building={<BuildingField register={register} />}
                     flat={<FlatField register={register} />}
                     postcode={<PostcodeField register={register} countryIsoCode={Country.DEFAULT.getIsoCode()} />}
                     street={<StreetField register={register} />}
                     town={<TownField register={register} />}
                  />
                  <Grid<DETAILS_GRID_AREA, typeof DETAILS_GRID_COLUMNS>
                     areas={DETAILS_GRID_AREAS}
                     testId={'detailsGrid'}
                     bankAccountNumber={<BankAccountNumberField register={register} />}
                     bankName={<BankNameField register={register} />}
                     bdoNumber={<BdoNumberField register={register} />}
                     taxIdentifier={<TaxIdentifierField register={register} />}
                  />
               </StyledFlex>
            </div>
            <div>
               <Heading
                  content={invoiceCreateAccountModalTranslationKeys.invoiceLoginDetails}
                  testId={'invoiceLoginDetails'}
                  type='h2'
               />
               <Grid<LOGIN_DETAILS_GRID_AREA, typeof LOGIN_DETAILS_GRID_COLUMNS>
                  areas={LOGIN_DETAILS_GRID_AREAS}
                  testId={'loginDetailsGrid'}
                  email={<EmailField register={register} />}
                  password={<PasswordField register={register} />}
               />
               <InvoiceLegalAndComplianceLoader>
                  {({ invoiceLegalAndCompliance }) => (
                     <StyledFlex direction='column' gap={12}>
                        <InvoiceCreateAccountFormTermsAndConditionsCheckbox
                           register={register}
                           termsAndConditionsUrl={invoiceLegalAndCompliance.termsAndConditionsUrl}
                        />
                        <InvoiceCreateAccountFormPrivacyPolicyAndGdprCheckbox
                           register={register}
                           privacyPolicyAndGdprUrl={invoiceLegalAndCompliance.privacyPolicyAndGdprUrl}
                        />
                     </StyledFlex>
                  )}
               </InvoiceLegalAndComplianceLoader>
            </div>
         </StyledFlex>

         {children({
            handleSubmit: handleSubmit,
            formState: formState,
         })}
      </Form>
   );
};
